import create from 'zustand';
import { persist } from 'zustand/middleware';
import steps, { StepDataMap, StepProps, stepComponents } from '../components/RegistrationStepper/steps';

/**
 * @module
 *
 * This module exports `useRegistrationStore`, a Zustand store designed to manage the state of a multi-step registration process.
 *
 * The store tracks the current step, user-provided data, validation statuses for each step, and any dynamically added steps.
 * It offers methods to navigate between steps, update user data, validate steps, add or remove dynamic steps, and reset the state.
 *
 * ## Key Features
 * - Step navigation (next, previous, specific step)
 * - User data management per step
 * - Step validation tracking
 * - Dynamic step management (add/remove steps)
 * - State reset to initial configuration
 *
 */

interface RegistrationState {
  currentStep: number;
  userData: StepDataMap;
  stepsValidation: { [key: number]: boolean };
  dynamicSteps: Array<{ key: keyof StepDataMap, component?: React.ComponentType<StepProps> }>;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  updateUserData: <K extends keyof StepDataMap>(step: K, data: StepDataMap[K]) => void;
  markStepValidated: (step: number, isValid?: boolean) => void;
  removeStep: (stepKey: keyof StepDataMap) => void;
  addStep: (stepKey: keyof StepDataMap, component: React.ComponentType<StepProps>) => void;
  resetState: () => void;
}

/**
 * Maps components to their respective steps.
 *
 * @param stepsArray - The array of steps to map components to.
 * @returns The array of steps with their respective components.
 */
const mapComponentsToSteps = (stepsArray: Array<{ key: keyof StepDataMap }>) => {
  return stepsArray.map(step => ({
    key: step.key,
    component: stepComponents[step.key] || steps.find(s => s.key === step.key)?.component
  }));
};

/**
 * The initial state of the registration store.
 *
 * @remarks
 * This state includes the current step, user data, step validation status, and dynamic steps with their respective components.
 */
const initialState = {
  currentStep: 0,
  userData: {} as StepDataMap,
  stepsValidation: {},
  dynamicSteps: mapComponentsToSteps(steps),
};

/**
 * Zustand store for managing the state of the registration stepper.
 *
 * @example
 * Here's an example of how to use the `useRegistrationStore`:
 * ```tsx
 * const { currentStep, nextStep, prevStep } = useRegistrationStore();
 * ```
 *
 * @public
 */
export const useRegistrationStore = create<RegistrationState>()(
  persist(
    (set) => ({
      ...initialState,

      /**
       * Advances to the next step.
       */
      nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),

      /**
       * Goes back to the previous step.
       */
      prevStep: () => set((state) => ({ currentStep: state.currentStep > 0 ? state.currentStep - 1 : 0 })),

      /**
       * Navigates to a specific step.
       *
       * @param step - The step number to navigate to.
       */
      goToStep: (step: number) => set(() => ({ currentStep: step })),

      /**
       * Updates the user data for a specific step.
       *
       * @param step - The key of the step to update.
       * @param data - The data to update for the step.
       */
      updateUserData: (step, data) => set((state) => ({
        userData: {
          ...state.userData,
          [step]: data,
        },
      })),

      /**
       * Marks a step as validated or invalidated.
       *
       * @param step - The step number to mark as validated.
       * @param isValid - Whether the step is valid. Defaults to `true`.
       */
      markStepValidated: (step, isValid = true) => set((state) => ({
        stepsValidation: {
          ...state.stepsValidation,
          [step]: isValid,
        },
      })),

      /**
       * Removes a step from the dynamic steps.
       *
       * @param stepKey - The key of the step to remove.
       */
      removeStep: (stepKey) => set((state) => {
        const newDynamicSteps = state.dynamicSteps.filter(step => step.key !== stepKey);
        return {
          dynamicSteps: newDynamicSteps,
          currentStep: state.currentStep >= newDynamicSteps.length ? newDynamicSteps.length - 1 : state.currentStep
        };
      }),

      /**
       * Adds a new step to the dynamic steps.
       *
       * @param stepKey - The key of the step to add.
       * @param component - The component for the step.
       */
      addStep: (stepKey, component) => {
        stepComponents[stepKey] = component;
        set((state) => ({
          dynamicSteps: [...state.dynamicSteps, { key: stepKey, component }]
        }));
      },

      /**
       * Resets the state to its initial values.
       */
      resetState: () => set(() => ({
        ...initialState,
        dynamicSteps: mapComponentsToSteps(steps)
      })),
    }),
    {
      name: 'registration-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.dynamicSteps = mapComponentsToSteps(state.dynamicSteps);
        }
      },
    }
  )
);

export default useRegistrationStore;
